import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Note } from '@/types/notepad';
import {
  useToggleNotePin,
  useToggleNoteArchive,
  useDeleteNote,
  useRestoreNote,
} from '@/hooks/useMyNotes';
import { useDeleteConfirmMode } from '@/hooks/useNotepadPreferences';
import { useNotepadEditor } from './useNotepadEditorState';
import { InlineDeleteConfirm } from './InlineDeleteConfirm';
import { notepadToast } from '@/lib/toast/notepadToast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Pin, Archive, MoreVertical, Pencil, Trash2, CheckSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface NoteCardProps {
  note: Note;
  isSelected?: boolean;
  onClick?: () => void;
}

/**
 * Note card component
 * Shows title, preview, tags, pinned status
 * Actions: Pin, Archive, Edit, Delete
 */
export function NoteCard({ note, isSelected, onClick }: NoteCardProps) {
  const { t } = useTranslation(['notepad', 'common']);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { isUndoOnlyMode } = useDeleteConfirmMode();
  const { openEdit } = useNotepadEditor();
  const togglePin = useToggleNotePin();
  const toggleArchive = useToggleNoteArchive();
  const deleteNote = useDeleteNote();
  const restoreNote = useRestoreNote();

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openEdit('note', note.id);
    },
    [openEdit, note.id]
  );

  const handleTogglePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    togglePin.mutate(note.id);
  };

  const handleToggleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleArchive.mutate(note.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isUndoOnlyMode) {
      // Undo-only mode: immediately delete with toast
      handleConfirmDelete();
    } else {
      // Inline mode: show confirmation
      setDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    setDeleteConfirmOpen(false);

    deleteNote.mutate(note.id, {
      onSuccess: () => {
        // Show undo toast with 5 second duration
        notepadToast.noteDeleted(note.title, () => {
          // Restore note by clearing deletedAt
          restoreNote.mutate(note.id, {
            onSuccess: () => {
              setIsDeleting(false);
            },
          });
        });
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!deleteConfirmOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setDeleteConfirmOpen(false);
        // Return focus to menu button
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [deleteConfirmOpen]);

  // Preview text (first 100 chars of content)
  const preview = note.content.slice(0, 100) + (note.content.length > 100 ? '...' : '');

  // Checklist summary
  const checklistCount = note.checklist?.length || 0;
  const checklistDone = note.checklist?.filter((item) => item.done).length || 0;

  // Don't render if deleting (optimistic removal)
  if (isDeleting) return null;

  return (
    <motion.div
      ref={cardRef}
      layout
      onClick={deleteConfirmOpen ? undefined : onClick}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.18 },
        height: { duration: 0.18 },
      }}
      whileHover={deleteConfirmOpen ? undefined : { scale: 1.01 }}
      whileTap={deleteConfirmOpen ? undefined : { scale: 0.99 }}
      className={`group relative rounded-xl border bg-card transition-all duration-200 ${
        deleteConfirmOpen
          ? 'isolate z-[35] shadow-md'
          : 'hover:bg-muted/50 cursor-pointer hover:shadow-sm'
      } ${isSelected ? 'ring-2 ring-primary' : ''}`}
    >
      {/* Show inline confirmation or normal content */}
      <AnimatePresence mode="wait">
        {deleteConfirmOpen ? (
          <InlineDeleteConfirm
            key="confirm"
            isOpen={deleteConfirmOpen}
            title={note.title}
            type="note"
            onConfirm={handleConfirmDelete}
            onCancel={() => {
              setDeleteConfirmOpen(false);
              // Return focus to menu button
              menuButtonRef.current?.focus();
            }}
          />
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="p-4"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {note.pinned && (
                  <Pin className="h-4 w-4 text-primary flex-shrink-0" fill="currentColor" />
                )}
                <h4 className="text-sm font-semibold truncate">{note.title}</h4>
              </div>

              {/* Actions menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    ref={menuButtonRef}
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                    aria-label={t('accessibility.noteActions')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleTogglePin}>
                    <Pin className="mr-2 h-4 w-4" />
                    {note.pinned ? t('notepad:actions.unpin') : t('notepad:actions.pin')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleEdit}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t('notepad:actions.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleToggleArchive}>
                    <Archive className="mr-2 h-4 w-4" />
                    {note.archived ? t('notepad:actions.unarchive') : t('notepad:actions.archive')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('notepad:actions.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Preview */}
            {preview && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{preview}</p>
            )}

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Checklist summary */}
              {checklistCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckSquare className="h-3 w-3" />
                  <span>
                    {checklistDone}/{checklistCount}
                  </span>
                </div>
              )}

              {/* Tags */}
              {note.tags?.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  #{tag}
                </Badge>
              ))}
              {note.tags && note.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{note.tags.length - 3}
                </Badge>
              )}

              {/* Updated time */}
              {note.updatedAt && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-xs text-muted-foreground ml-auto"
                >
                  {formatDistanceToNow(note.updatedAt.toDate(), { addSuffix: true })}
                </motion.span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
